import React, { useState } from "react"
import { CodeTextEditor } from "./code-text-editor"

/**
 * JSON editor that works with json-object-values
 * - user formatting *not* retained
 * - changes are only emitted if the JSON is valid
 */
export function JsonEditor(props: {
  name?: string,
  exportFileName?: string,
  value: unknown,
  onValueChange: (str: unknown) => void
}) {
  const valueAsString = props.value === undefined ? '' : JSON.stringify(props.value, null, 4)
  const [ draftValue, setDraftValue ] = useState(valueAsString)
  const lastValueRef = React.useRef<unknown>()

  React.useEffect(() => {
    // sync `draftValue` with external changes to `props.value`
    if (lastValueRef.current !== props.value) {
      lastValueRef.current = props.value
      setDraftValue(valueAsString)
    }
  }, [props.value, valueAsString])

  const onChange = (valueString: string) => {
    setDraftValue(valueString)
    try {
      const value = JSON.parse(valueString)
      props.onValueChange(value)
    } catch (e) {
      // ignore
    }
  }

  return (
    <JsonTextEditor name={props.name}
                    exportFileName={props.exportFileName}
                    value={draftValue}
                    onValueChange={onChange} />
  )
}

/**
 * JSON editor that works for json-strings
 * - user formatting retained
 * - changes are emitted whether the JSON is valid or not
 */
export function JsonTextEditor(props: {
  name?: string,
  exportFileName?: string,
  value: string,
  onValueChange?: (str: string) => void
  readonly?: boolean
}) {
  return (
    <CodeTextEditor
      name={props.name}
      exportFileName={props.exportFileName}
      value={props.value}
      onValueChange={props.onValueChange}
      readonly={props.readonly}
      mode="json"
      fileExtension="json"
      mimeType="application/json"
      validate={checkJson}
      format={formatJson}
    />
  )
}

function checkJson(value: string): 'ok' | string {
  try {
    JSON.parse(value)
    return 'ok'
  } catch (e) {
    return String(e)
  }
}

/** Leaves the value untouched when it does not parse, so the button cannot destroy a draft. */
function formatJson(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 4)
  } catch (e) {
    return value
  }
}
